import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "./lib/api";
import { initPushNotifications } from "./lib/push";
import { autoPromptIfNeeded as autoPromptWebPush } from "./lib/webpush";
import { supabase, supabaseConfigured, isInviteFlow } from "./lib/supabase";
import { useAuth, useEffectiveUser } from "./lib/auth";
import { can, canSeeBeta, isPlatformDeveloper } from "./lib/permissions";
import { Layout } from "./components/Layout";
import { Dashboard } from "./pages/Dashboard";
import { Schedule } from "./pages/Schedule";
import { Employees } from "./pages/Employees";
import { Shifts } from "./pages/Shifts";
import { TimeOffPage } from "./pages/TimeOff";
import { TrainingPage } from "./pages/Training";
import { FormsPage } from "./pages/Forms";
import { Settings } from "./pages/Settings";
import { Audit } from "./pages/Audit";
import { Records } from "./pages/Records";
import { ShiftBid } from "./pages/ShiftBid";
import { OnCall } from "./pages/OnCall";
import { MockSchedulesIndex, MockScheduleEditor } from "./pages/MockSchedules";
import { Login } from "./pages/Login";
import { Subscribe } from "./pages/Subscribe";
import { Welcome } from "./pages/Welcome";
import { AcceptInvite } from "./pages/AcceptInvite";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { Support } from "./pages/Support";
import { Landing } from "./pages/Landing";
import { Changelog } from "./pages/Changelog";
import { isNative } from "./lib/runtime";

export default function App() {
  const user = useAuth((s) => s.user);
  const effective = useEffectiveUser();
  const setUser = useAuth((s) => s.setUser);
  const [booted, setBooted] = useState(false);
  const location = useLocation();
  // Agency-wide "which optional features are disabled" list. Feeds
  // the per-route gates below so a bookmarked / deep-linked URL to
  // a turned-off feature (Time Off, Training, Audit, On-Call) bounces
  // to the Dashboard instead of loading the page.
  const agencyForGates = useQuery({
    queryKey: ["agency"],
    queryFn: api.getAgency,
    enabled: !!user,
  });
  const disabledFeatures = new Set(agencyForGates.data?.disabledFeatures ?? []);
  // Deep-link invite-mode flag. On the web, isInviteFlow() snapshots
  // the URL hash at module load and we render AcceptInvite if it
  // contains type=invite/recovery/magiclink. In a Capacitor iOS
  // build there is no URL hash — the deep link arrives later via
  // @capacitor/app's appUrlOpen / getLaunchUrl, so we have to flip
  // this flag from the listener too.
  const [inviteMode, setInviteMode] = useState(() => isInviteFlow());

  // Capacitor deep-link handler: a Supabase invite / recovery email
  // configured to redirect to shiftmanager://… opens the app, and
  // the App plugin delivers the URL here. We extract the auth tokens
  // from the hash, hand them to supabase.auth.setSession, and flip
  // inviteMode so the AcceptInvite page renders the password form.
  // On web (no Capacitor runtime) the import resolves to a no-op
  // shim and the listener never fires — the regular hash-based
  // detection still does the work.
  useEffect(() => {
    let mounted = true;
    let listenerHandle: { remove: () => Promise<void> } | null = null;

    async function processUrl(url: string) {
      const hashIdx = url.indexOf("#");
      if (hashIdx === -1) return;
      const params = new URLSearchParams(url.slice(hashIdx + 1));
      const access_token = params.get("access_token");
      const refresh_token = params.get("refresh_token");
      const type = params.get("type");
      if (!access_token || !refresh_token) return;
      const { error } = await supabase.auth.setSession({
        access_token, refresh_token,
      });
      if (error) {
        // eslint-disable-next-line no-console
        console.warn("setSession from deep link failed:", error.message);
        return;
      }
      if (type === "invite" || type === "recovery" || type === "magiclink") {
        if (mounted) setInviteMode(true);
      }
    }

    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        // Cold-start case: the URL the app launched with.
        const launch = await CapApp.getLaunchUrl();
        if (launch?.url) await processUrl(launch.url);
        // And every subsequent deep link while the app is running.
        listenerHandle = await CapApp.addListener("appUrlOpen", (data) => {
          void processUrl(data.url);
        });
      } catch {
        /* Capacitor not present — running on the web. */
      }
    })();

    return () => {
      mounted = false;
      listenerHandle?.remove();
    };
  }, []);

  // Resume any existing session. Boot has a hard 4s ceiling so a slow
  // network or stale token can never leave us stuck on "Loading…".
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      if (!cancelled) setBooted(true);
    }, 4000);

    api.currentUser().then((u) => {
      if (cancelled) return;
      if (u) setUser(u);
      setBooted(true);
      clearTimeout(timer);
    }).catch(() => {
      if (!cancelled) setBooted(true);
      clearTimeout(timer);
    });

    if (!supabaseConfigured) {
      return () => { cancelled = true; clearTimeout(timer); };
    }

    // supabase-js holds a single mutex across the whole
    // _notifyAllSubscribers call. Calling any auth method (getSession,
    // updateUser, setSession, …) from inside this callback deadlocks
    // the caller: the lock isn't released until every subscriber has
    // returned, but the subscriber is waiting on the lock. Defer via
    // setTimeout so the callback returns synchronously and the lock
    // drops, then do the profile lookup.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      setTimeout(async () => {
        if (cancelled) return;
        const u = await api.currentUser();
        if (cancelled) return;
        setUser(u);
        setBooted(true);
      }, 0);
    });
    return () => {
      cancelled = true;
      clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, [setUser]);

  // Register push channels once we have a signed-in user. Both
  // calls are idempotent and self-gating:
  //   - initPushNotifications short-circuits on web (no Capacitor)
  //   - autoPromptWebPush short-circuits on Capacitor + on browsers
  //     that lack Push. On first sign-in in a supported browser it
  //     fires the system permission dialog; subsequent sign-ins
  //     just refresh the subscription if granted. The auto-prompt
  //     honors a local "explicitly disabled in Settings" flag so a
  //     user who opted out doesn't get re-pestered on every refresh.
  useEffect(() => {
    if (!user) return;
    void initPushNotifications();
    void autoPromptWebPush();
  }, [user]);

  // Auto-hop the platform developer from production to /preview/
  // whenever the preview build's SHA is ahead of prod's. Keeps me
  // from ever accidentally testing on stable when there's an
  // unreleased build waiting for review.
  //
  // Runs only when: not native, on the stable channel, signed in as
  // the platform developer, and neither ?stable=1 in the URL nor a
  // session-scoped "stay here" flag is set. The /preview/version.json
  // fetch fails silently if there's no preview build yet.
  useEffect(() => {
    if (isNative) return;
    if (__BUILD_CHANNEL__ !== "stable") return;
    if (!isPlatformDeveloper(user)) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.has("stable")) {
      // Remember the intent for the rest of the tab session so a
      // background reload doesn't bounce us back to preview.
      sessionStorage.setItem("smStayOnStable", "1");
      return;
    }
    if (sessionStorage.getItem("smStayOnStable") === "1") return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/preview/version.json", { cache: "no-store" });
        if (!r.ok) return;
        const data = await r.json() as { sha?: string; channel?: string };
        if (cancelled) return;
        if (!data.sha || data.sha === __BUILD_SHA__) return;
        // Preserve the current hash-route so we land on the same
        // page in preview instead of dumping the dev on Dashboard.
        window.location.replace("/preview/" + (window.location.hash ?? ""));
      } catch {
        /* preview offline / build missing / network hiccup — stay put */
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!booted) {
    return <BrandedLoading />;
  }

  // Privacy / legal / support pages are reachable without sign-in
  // so Apple's App Store reviewers (and anyone clicking the policy
  // or support link in App Store Connect) can read them without
  // an account. Has to sit above both the inviteMode and the
  // !user branches.
  const path = location.pathname;
  if (path === "/privacy") return <PrivacyPolicy />;
  if (path === "/support") return <Support />;

  // Invite / recovery / magic-link landings: either the hash captured
  // synchronously at module load (web), or the Capacitor deep-link
  // handler above flipped inviteMode after extracting tokens from
  // the appUrlOpen event (iOS).
  if (inviteMode) {
    return <AcceptInvite />;
  }

  // Unauthenticated routing:
  //   /login  → email/password sign-in
  //   /welcome → "check your email" landing after Stripe checkout
  //   anything else → marketing landing page (Subscribe + Sign in)
  if (!user) return <UnauthenticatedShell />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/schedule/:deptId" element={<Schedule />} />
        {/* Employees + Shifts are supervisor tools now (L2+). Rank-
          * and-file who bookmarked the URLs bounce back to the
          * dashboard instead of a bare table they can't actually
          * edit. */}
        <Route path="/employees" element={!can.editRoster(effective) ? <Navigate to="/" replace /> : <Employees />} />
        <Route path="/shifts" element={!can.editRoster(effective) ? <Navigate to="/" replace /> : <Shifts />} />
        <Route path="/time-off" element={disabledFeatures.has("time_off") ? <Navigate to="/" replace /> : <TimeOffPage />} />
        <Route path="/training" element={disabledFeatures.has("training") ? <Navigate to="/" replace /> : <TrainingPage />} />
        {/* Custom form pages defined in Settings → Forms. FormsPage
          * itself handles the "disabled in Sections & display" case
          * (it looks the form up by slug and redirects if missing or
          * disabled) so we don't have to fan out N routes here. */}
        <Route path="/forms/:slug" element={<FormsPage />} />
        <Route path="/audit" element={disabledFeatures.has("audit") || !can.viewAudit(effective) ? <Navigate to="/" replace /> : <Audit />} />
        {/* Records — running hours per employee per pay period.
          * L2+ only, beta-gated while it's still in test, and
          * honors the "records" agency feature toggle. */}
        <Route path="/records" element={disabledFeatures.has("records") || !can.editRoster(effective) || !canSeeBeta(effective) ? <Navigate to="/" replace /> : <Records />} />
        {/* Shift Bid — 2-week bid sheets built by L2+, claimed by
          * officers in seniority order. Visible to everyone in the
          * agency (officers need to see + claim rows). Beta-gated
          * while it's still in test, and honors the "shift_bid"
          * feature toggle. */}
        <Route path="/shift-bid" element={disabledFeatures.has("shift_bid") || !canSeeBeta(effective) ? <Navigate to="/" replace /> : <ShiftBid />} />
        {/* On-Call gating happens inside the page (feature flag +
          * owner check during beta) OR when the agency turned On-
          * Call off in Settings. */}
        <Route path="/on-call" element={disabledFeatures.has("on_call") ? <Navigate to="/" replace /> : <OnCall />} />
        <Route path="/mock-schedules" element={!can.seeMockSchedules(effective) || !canSeeBeta(effective) ? <Navigate to="/" replace /> : <MockSchedulesIndex />} />
        <Route path="/mock-schedules/:id" element={!can.seeMockSchedules(effective) || !canSeeBeta(effective) ? <Navigate to="/" replace /> : <MockScheduleEditor />} />
        <Route path="/settings" element={can.viewSettings(effective) ? <Settings /> : <Dashboard />} />
        <Route path="/changelog" element={<Changelog />} />
        {/* Any path we don't recognise — most commonly /login or
          * /welcome after a successful sign-in or checkout —
          * funnels back to the dashboard. Without this catch-all
          * an authenticated user who lands on /login (e.g. by
          * clicking sign-in from the marketing page and finishing
          * auth) sees a blank screen because no route matches. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

// Routing shell for visitors with no Supabase session.
//
//   /             → Login (the default landing surface — every
//                   unauthenticated visitor sees the sign-in form
//                   first; invited officers don't have to wade
//                   through marketing copy to reach it)
//   /start        → Subscribe (the paid signup page, linked from
//                   the bottom of Login on web only)
//   /welcome      → Welcome (Stripe redirects here after checkout
//                   to tell the user to check their email)
//   anything else → Login (catch-all, same as /)
// Branded splash shown while the app boots and resolves auth. On
// iOS this paints immediately after Apple's static launch screen,
// so a returning user sees their agency patch from the very first
// frame after dismissal — no white-flash gap.
//
// Source order: cached agency brand image (from a prior fetch) >
// ShiftManager wordmark. The cache is populated by api.getBrandImage
// on every successful fetch, so by the second app launch the
// patch is always available.
function BrandedLoading() {
  let brand: string | null = null;
  try { brand = localStorage.getItem("shift_manager.brand_image"); }
  catch { /* localStorage blocked */ }
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col items-center justify-center gap-6 px-6">
      {brand ? (
        <img
          src={brand}
          alt=""
          className="max-h-48 w-auto object-contain opacity-95 select-none"
          draggable={false}
        />
      ) : (
        <div className="text-2xl font-semibold tracking-tight text-slate-700 select-none">
          ShiftManager
        </div>
      )}
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <span className="inline-block w-3 h-3 rounded-full border-2 border-slate-300 border-t-slate-500 animate-spin" />
        Loading…
      </div>
    </div>
  );
}

function UnauthenticatedShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  if (path === "/start") {
    return <Subscribe onGoToLogin={() => navigate("/login")} />;
  }
  if (path === "/welcome") {
    return <Welcome />;
  }
  if (path === "/login") {
    return <Login />;
  }
  // Root path (/) diverges by platform:
  //   - Web: public marketing Landing page. Gives Apple reviewers
  //     and cold visitors a real product-explains-itself surface
  //     before dropping them into a sign-in form.
  //   - iOS Capacitor: skip the marketing — the user installed the
  //     app to sign in, not to read pitch copy. Go straight to Login.
  if (isNative) return <Login />;
  return <Landing />;
}
